import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface Material {
  id: string;
  name: string;
  unit: string;
}

const MaterialsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [formData, setFormData] = useState({ name: '', unit: '' });

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    try {
      const res = await api.get('/reference/materials');
      setMaterials(res.data);
    } catch (err) {
      console.error('Failed to load materials:', err);
    }
  };

  const handleOpenDialog = (material?: Material) => {
    if (material) {
      setEditingMaterial(material);
      setFormData({ name: material.name, unit: material.unit });
    } else {
      setEditingMaterial(null);
      setFormData({ name: '', unit: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingMaterial) {
        await api.put(`/reference/materials/${editingMaterial.id}`, formData);
      } else {
        await api.post('/reference/materials', formData);
      }
      setDialogOpen(false);
      loadMaterials();
    } catch (err) {
      console.error('Failed to save material:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('admin.deleteMaterialConfirm'))) return;
    try {
      await api.delete(`/reference/materials/${id}`);
      loadMaterials();
    } catch (err) {
      console.error('Failed to delete material:', err);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        {t('admin.addMaterial')}
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell>{t('admin.unit')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map((material) => (
              <TableRow key={material.id}>
                <TableCell>{material.name}</TableCell>
                <TableCell>{material.unit}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(material)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(material.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMaterial ? t('admin.editMaterial') : t('admin.addMaterial')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('common.name')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('admin.unit')}
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            sx={{ mt: 2 }}
            placeholder={t('admin.unitPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialsManagement;



